import React, { useState, useRef } from "react";
import Papa from "papaparse";
import { MdArrowForward, MdDownload, MdUploadFile } from "react-icons/md";
import { getApiErrorMessage } from "api";
import AlertPane from "components/alert";
import Badge from "components/badge";
import Button from "components/button";
import Dialog from "components/dialog";
import { Checkbox, Select } from "components/fields";
import notify from "lib/notify";
import pathwaysService from "services/pathwaysService";
import { EMPTY } from "utils/format";

const TEMPLATE_CSV =
  "First Name,Last Name,Email,Phone,DOB,Membership\n" +
  "Jane,Doe,jane.doe@example.com,+12145551212,1990-04-15,Visitor\n";

const FIELDS = [
  { key: "firstName", label: "First Name", required: true },
  { key: "lastName", label: "Last Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "dob", label: "DOB" },
  { key: "membership", label: "Membership" },
];

const ROW_STATUS_TONES = { new: "success", duplicate: "neutral", error: "danger" };

const capitalize = (value = "") => value.charAt(0).toUpperCase() + value.slice(1);

export default function CsvImportModal({ open, onClose, onImported, onComplete }) {
  const [step, setStep] = useState(1); // 1 upload, 2 mapping, 3 preview, 4 done
  const [rawRows, setRawRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [result, setResult] = useState(null);
  const [fileError, setFileError] = useState("");
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const reset = () => {
    setStep(1); setRawRows([]); setHeaders([]); setMapping({});
    setPreview(null); setResult(null); setFileError(""); setUpdateExisting(false); setBusy(false);
    setFileName(""); setDragActive(false);
  };

  const closeAndReset = () => { reset(); onClose(); };

  // Finish the import: hand the result to onComplete (lets the onboarding flow
  // advance to the next step) before resetting/closing.
  const finishImport = () => {
    const r = result;
    closeAndReset();
    onComplete?.(r);
  };

  const parseFile = (file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      setFileError("Choose a .csv file.");
      return;
    }
    setFileError("");
    setFileName(file.name);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (parsed) => {
        const hdrs = (parsed.meta?.fields || []).filter(Boolean);
        setHeaders(hdrs);
        setRawRows(parsed.data || []);
        try {
          const { data } = await pathwaysService.detectCsvColumns(hdrs);
          setMapping(data?.data || {});
        } catch {
          setMapping({});
        }
        setStep(2);
      },
      error: (err) => setFileError(`Could not read the CSV file: ${err.message}`),
    });
  };

  const onFileInput = (e) => parseFile(e.target.files?.[0]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    parseFile(e.dataTransfer?.files?.[0]);
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "pathways-guests-template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const buildMappedRows = () => rawRows.map((r) => {
    const out = {};
    for (const f of FIELDS) {
      const src = mapping[f.key];
      if (src) out[f.key] = r[src] ?? "";
    }
    return out;
  });

  const runPreview = async () => {
    setBusy(true);
    try {
      const rows = buildMappedRows();
      const { data } = await pathwaysService.previewCsvImport(rows);
      setPreview(data?.data);
      setStep(3);
    } catch (e) {
      notify.error(getApiErrorMessage(e, "Could not preview the import"));
    } finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const rows = buildMappedRows();
      const { data } = await pathwaysService.importCsv(rows, updateExisting);
      setResult(data?.data);
      setStep(4);
      notify.success("Guests imported");
      onImported?.(data?.data);
    } catch (e) {
      notify.error(getApiErrorMessage(e, "Could not import guests"));
    } finally { setBusy(false); }
  };

  const mappingIncomplete = !mapping.firstName || (!mapping.email && !mapping.phone);
  const importCount = preview
    ? preview.summary.new + (updateExisting ? preview.summary.duplicate : 0)
    : 0;

  let footer = null;
  if (step === 1) {
    footer = (
      <Button variant="secondary" onClick={closeAndReset}>
        Cancel
      </Button>
    );
  } else if (step === 2) {
    footer = (
      <>
        <Button variant="secondary" onClick={() => setStep(1)} disabled={busy}>
          Back
        </Button>
        <Button onClick={runPreview} loading={busy} disabled={mappingIncomplete}>
          {busy ? "Previewing…" : "Preview"}
        </Button>
      </>
    );
  } else if (step === 3 && preview) {
    footer = (
      <>
        <Button variant="secondary" onClick={() => setStep(2)} disabled={busy}>
          Back
        </Button>
        <Button onClick={commit} loading={busy} disabled={importCount === 0}>
          {busy ? "Importing…" : `Import ${importCount} ${importCount === 1 ? "Row" : "Rows"}`}
        </Button>
      </>
    );
  } else if (step === 4 && result) {
    footer = (
      <Button
        onClick={finishImport}
        rightIcon={onComplete ? <MdArrowForward aria-hidden="true" /> : undefined}
      >
        {onComplete ? "Continue" : "Done"}
      </Button>
    );
  }

  return (
    <Dialog
      isOpen={open}
      onClose={closeAndReset}
      title="Import Guests from CSV"
      size="lg"
      footer={footer}
    >
      {fileError && (
        <AlertPane tone="danger">{fileError}</AlertPane>
      )}

      {step === 1 && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={onFileInput}
            className="hidden"
          />

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
            onDrop={onDrop}
            className={`focus-ring flex w-full flex-col items-center justify-center gap-3 rounded-card border-2 border-dashed px-6 py-10 text-center transition-colors ${
              dragActive
                ? "border-primary bg-brand-50 dark:border-accent dark:bg-brand-900/20"
                : "border-line hover:bg-surface-subtle"
            }`}
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200">
              <MdUploadFile aria-hidden="true" className="h-6 w-6" />
            </span>
            <span className="text-sm">
              <span className="font-semibold text-fg">Click to choose a CSV file</span>
              <span className="text-fg-muted"> or drag and drop it here</span>
            </span>
            <span className="text-xs text-fg-muted">CSV files only, up to a few thousand rows</span>
          </button>

          {fileName && (
            <p className="mt-3 text-center text-sm text-fg-muted">
              Selected: <span className="font-medium text-fg">{fileName}</span>
            </p>
          )}

          <div className="mt-4 flex items-center justify-between gap-3 text-xs text-fg-muted">
            <span>
              Expected columns: First Name, Last Name, Email, Phone, DOB, Membership.
              We&apos;ll auto-match them next.
            </span>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<MdDownload aria-hidden="true" />}
              onClick={downloadTemplate}
            >
              Download Template
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-fg-muted">
            Confirm the column mapping. Required: First Name plus Email <em>or</em> Phone.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {FIELDS.map((f) => (
              <Select
                key={f.key}
                label={f.label}
                required={f.required}
                value={mapping[f.key] || ""}
                onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value || null })}
              >
                <option value="">Skip</option>
                {headers.map((h) => <option key={h} value={h}>{h}</option>)}
              </Select>
            ))}
          </div>
          {mappingIncomplete && (
            <AlertPane tone="warning" className="">
              Map First Name and at least one of Email or Phone to continue.
            </AlertPane>
          )}
        </div>
      )}

      {step === 3 && preview && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-fg">
            <strong>{preview.summary.new}</strong> new ·{" "}
            <strong>{preview.summary.duplicate}</strong> duplicate ·{" "}
            <strong>{preview.summary.error}</strong> error
          </p>
          <Checkbox
            label="Update matched records"
            description="Fills blank fields only and never overwrites filled fields."
            checked={updateExisting}
            onChange={(e) => setUpdateExisting(e.target.checked)}
          />
          <div className="max-h-64 overflow-y-auto rounded-control border border-line">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-surface-raised">
                <tr>
                  <th className="p-2 text-left font-medium uppercase tracking-wide text-fg-muted">Status</th>
                  <th className="p-2 text-left font-medium uppercase tracking-wide text-fg-muted">Name</th>
                  <th className="p-2 text-left font-medium uppercase tracking-wide text-fg-muted">Email / Phone</th>
                  <th className="p-2 text-left font-medium uppercase tracking-wide text-fg-muted">Reason</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.index} className="border-t border-line">
                    <td className="p-2">
                      <Badge tone={ROW_STATUS_TONES[r.status] || "neutral"} size="sm">
                        {capitalize(r.status)}
                      </Badge>
                    </td>
                    <td className="p-2 text-fg">
                      {r.row.firstName} {r.row.lastName}
                    </td>
                    <td className="p-2 text-fg">{r.row.email || r.row.phone || EMPTY}</td>
                    <td className="p-2 text-fg-muted">{r.reason || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {step === 4 && result && (
        <p className="text-sm text-fg">
          Imported: <strong>{result.created}</strong> created ·{" "}
          <strong>{result.updated}</strong> updated ·{" "}
          <strong>{result.skipped}</strong> skipped ·{" "}
          <strong>{result.errors}</strong> errors
        </p>
      )}
    </Dialog>
  );
}
